/*
 * Чистая логика страницы «Парсинг»: фильтрация/сортировка снимка для отображения.
 * Никакой денежной логики — только показ (§1.2). Спред пересчитываем по ВИДИМЫМ
 * (выбранным) биржам, чтобы «Фандинг итог» соответствовал колонкам; при дефолте
 * (выбраны все биржи) это совпадает с серверным `funding_spread` (max−min).
 */

import { toNum } from '@/lib/format'
import type { ParserQuote, ParserSnapshot, NormalizedSymbol } from '@/lib/types'

/** Порог метрики/подсветки «Фандинг >X%». Зеркалит серверный дефолт
 *  `parser_funding_highlight_pct` (0.5%); редактирование порога — этап 17. */
export const HIGHLIGHT_PCT = 0.5

/** Дефолты фильтров — из серверных дефолтов конфига (§1.10). */
export const DEFAULT_MIN_VOLUME_USD = 20_000
export const DEFAULT_DISPLAY_LIMIT = 100

export interface ParserFilters {
  search: string
  /** |спред| ≥ этого (в процентах). 0 = не фильтровать. */
  minFundingPct: number
  /** Мин. суточный объём (USDT): строка проходит, если макс. объём её видимых
   *  котировок ≥ порога. Котировки без объёма (null) — пермиссивно (не отсекаем). */
  minVolumeUsd: number
  /** Подмножество бирж в области видимости (по умолчанию — все из снимка). */
  selectedExchanges: string[]
}

export interface SortState {
  /** 'coin' | 'spread' | `ex:<exchange>`. */
  key: string
  dir: 'asc' | 'desc'
}

export const DEFAULT_SORT: SortState = { key: 'spread', dir: 'desc' }

export interface DerivedRow {
  coin: string
  symbol: NormalizedSymbol
  quoteByExchange: Map<string, ParserQuote>
  /** Размах фандинга по видимым биржам (доля, ≥0). */
  spread: number
  soonestFundingMs: number
  soonestInterval: number
  /** Макс. суточный объём (USDT) среди видимых котировок; null — объём не отдан. */
  maxVolumeUsd: number | null
  isHot: boolean
}

function spreadOf(rates: number[]): number {
  if (rates.length < 2) return 0
  return Math.max(...rates) - Math.min(...rates)
}

/** Применить фильтры и вернуть производные строки + видимые колонки-биржи. */
export function deriveRows(
  snapshot: ParserSnapshot | null,
  filters: ParserFilters,
  sort: SortState,
): { rows: DerivedRow[]; columns: string[]; total: number } {
  if (!snapshot) return { rows: [], columns: [], total: 0 }

  const selected = new Set(filters.selectedExchanges)
  const columns = snapshot.exchanges.filter((e) => selected.has(e))
  const search = filters.search.trim().toLowerCase()
  const minFraction = filters.minFundingPct / 100
  const highlightFraction = HIGHLIGHT_PCT / 100

  const derived: DerivedRow[] = []
  for (const row of snapshot.rows) {
    const quoteByExchange = new Map<string, ParserQuote>()
    const rates: number[] = []
    let soonestFundingMs = 0
    let soonestInterval = 0
    let maxVolumeUsd: number | null = null
    for (const q of row.quotes) {
      if (!selected.has(q.exchange)) continue
      quoteByExchange.set(q.exchange, q)
      const r = toNum(q.funding_rate)
      if (r !== null) rates.push(r)
      const v = q.volume_24h_usd === null ? null : toNum(q.volume_24h_usd)
      if (v !== null) maxVolumeUsd = maxVolumeUsd === null ? v : Math.max(maxVolumeUsd, v)
      if (q.next_funding_ts_ms > 0) {
        if (soonestFundingMs === 0 || q.next_funding_ts_ms < soonestFundingMs) {
          soonestFundingMs = q.next_funding_ts_ms
          soonestInterval = q.interval_sec
        }
      }
    }
    // Нужна пара: ≥2 видимых биржи (связка — две ноги).
    if (quoteByExchange.size < 2) continue
    if (search && !row.coin.toLowerCase().includes(search)) continue

    const spread = spreadOf(rates)
    if (minFraction > 0 && spread < minFraction) continue
    // Фильтр по объёму: отсекаем только при ИЗВЕСТНОМ объёме ниже порога (null —
    // пермиссивно, как на бэкенде min_liquidity_usd).
    if (filters.minVolumeUsd > 0 && maxVolumeUsd !== null && maxVolumeUsd < filters.minVolumeUsd) {
      continue
    }

    derived.push({
      coin: row.coin,
      symbol: row.symbol,
      quoteByExchange,
      spread,
      soonestFundingMs,
      soonestInterval,
      maxVolumeUsd,
      isHot: spread >= highlightFraction,
    })
  }

  sortRows(derived, sort)
  return { rows: derived, columns, total: snapshot.rows.length }
}

function rowSortValue(row: DerivedRow, key: string): number | string | null {
  if (key === 'coin') return row.coin
  if (key === 'spread') return row.spread
  if (key === 'volume') return row.maxVolumeUsd
  if (key === 'funding') return row.soonestFundingMs > 0 ? row.soonestFundingMs : null
  if (key.startsWith('ex:')) {
    const ex = key.slice(3)
    const q = row.quoteByExchange.get(ex)
    return q ? toNum(q.funding_rate) : null
  }
  return null
}

function sortRows(rows: DerivedRow[], sort: SortState): void {
  const factor = sort.dir === 'asc' ? 1 : -1
  rows.sort((a, b) => {
    const va = rowSortValue(a, sort.key)
    const vb = rowSortValue(b, sort.key)
    // null (нет котировки на бирже) — всегда в конец, независимо от направления.
    if (va === null && vb === null) return a.coin.localeCompare(b.coin)
    if (va === null) return 1
    if (vb === null) return -1
    if (typeof va === 'string' || typeof vb === 'string') {
      return String(va).localeCompare(String(vb)) * factor
    }
    if (va === vb) return a.coin.localeCompare(b.coin)
    return (va - vb) * factor
  })
}
